/**
 * BRM access_token 刷新的跨标签页协调。
 *
 * 请求拦截器里的 `isRefreshing` 只是模块级变量，只能在单个标签页内去重。多个标签页同时
 * 越过 access_token 过期点时，会各自拿同一个 refresh_token 去换新 token；Keycloak 开启
 * refresh token 轮换后，后到的那次必然失败，用户会被弹「登录已失效」赶回登录页，实际上
 * 会话本身是好的。
 *
 * 本模块用 localStorage 做一把带 TTL 的互斥锁：抢到锁的标签页负责刷新，其余标签页等它把
 * 新 token 写回本地后直接复用；刷新失败时也先回读一次本地，若已被别的标签页换成新 token
 * 则按成功处理。
 *
 * @module utils/brmTokenRefreshLock
 */
import { LOCAL_STORAGE_KEY } from '@/constants';
import { storage, getStorageAuthInfo } from '@/utils/storageOperateApi';

/** 锁最长持有时间，超过即认为持锁标签页已崩溃或被关闭，允许他人抢占 */
const LOCK_TTL_MS = 20000;
/** 等待其它标签页刷新完成的最长时间 */
const WAIT_TIMEOUT_MS = 20000;
/** 等待期间回读本地鉴权的间隔 */
const WAIT_INTERVAL_MS = 200;
/** 生成锁标识随机串所用的进制 */
const OWNER_RADIX = 36;

/**
 * 读取本地当前的 BRM access_token。
 * @returns {string} access_token，取不到时为空串
 */
export function getStoredAccessToken() {
  const { brmToken } = getStorageAuthInfo() || {};
  return brmToken?.access_token || brmToken?.accessToken || brmToken?.token || '';
}

/**
 * 读取仍在有效期内的锁。
 * @returns {{ owner: string, at: number } | null} 有效锁，无锁或锁已过期返回 null
 */
function readValidLock() {
  const lock = storage.get(LOCAL_STORAGE_KEY.BRM_TOKEN_REFRESH_LOCK);
  // 锁过期说明持锁标签页已消失，视为无锁，避免所有标签页一起卡死
  if (lock?.at && Date.now() - lock.at <= LOCK_TTL_MS) return lock;
  return null;
}

/**
 * 尝试抢占刷新锁。
 * @param {string} owner - 本次刷新的唯一标识
 * @returns {boolean} 是否抢到锁
 */
function acquireLock(owner) {
  if (readValidLock()) return false;
  storage.set(LOCAL_STORAGE_KEY.BRM_TOKEN_REFRESH_LOCK, { owner, at: Date.now() });
  // localStorage 没有原子 CAS，写完回读一次确认没被并发的标签页覆盖
  return storage.get(LOCAL_STORAGE_KEY.BRM_TOKEN_REFRESH_LOCK)?.owner === owner;
}

/**
 * 释放自己持有的刷新锁。
 * @param {string} owner - 抢锁时使用的唯一标识
 * @returns {void}
 */
function releaseLock(owner) {
  const lock = storage.get(LOCAL_STORAGE_KEY.BRM_TOKEN_REFRESH_LOCK);
  // 只释放自己的锁，避免误删超时后抢占者刚写入的锁
  if (!lock || lock.owner === owner) storage.remove(LOCAL_STORAGE_KEY.BRM_TOKEN_REFRESH_LOCK);
}

/**
 * 等待持锁标签页把新的 access_token 写回本地。
 * 采用轮询而非 storage 事件：等待窗口很短，且部分内嵌 / 隐私模式下事件不可靠。
 * @param {string} previousToken - 等待前的 access_token
 * @returns {Promise<string>} 新的 access_token；持锁方刷新失败或等待超时返回空串
 */
function waitForOtherTabRefresh(previousToken) {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const latestToken = getStoredAccessToken();
      // token 已变化说明持锁标签页刷新成功，直接复用
      if (latestToken && latestToken !== previousToken) {
        clearInterval(timer);
        resolve(latestToken);
        return;
      }
      // 锁已释放但 token 没变，说明持锁方刷新失败，不必再等满超时
      if (!readValidLock()) {
        clearInterval(timer);
        resolve('');
        return;
      }
      // 持锁方迟迟没有结论，交回调用方自己刷新
      if (Date.now() - startedAt >= WAIT_TIMEOUT_MS) {
        clearInterval(timer);
        resolve('');
      }
    }, WAIT_INTERVAL_MS);
  });
}

/**
 * 以跨标签页互斥的方式刷新 BRM access_token。
 * @param {() => Promise<string>} doRefresh - 实际执行刷新并返回新 access_token 的回调
 * @returns {Promise<string>} 最新可用的 access_token
 * @example
 * const accessToken = await refreshAccessTokenExclusively(() => requestNewToken());
 */
export async function refreshAccessTokenExclusively(doRefresh) {
  const tokenBefore = getStoredAccessToken();
  const owner = `${Date.now()}-${Math.random().toString(OWNER_RADIX).slice(2)}`;

  // 抢不到锁说明别的标签页正在刷新，等它写回新 token 直接复用，不重复消耗 refresh_token
  if (!acquireLock(owner)) {
    const tokenFromOtherTab = await waitForOtherTabRefresh(tokenBefore);
    if (tokenFromOtherTab) return tokenFromOtherTab;
    // 持锁方刷新失败或已被关闭：强行接管后自己刷一次，由调用方决定失败后的处理
    storage.remove(LOCAL_STORAGE_KEY.BRM_TOKEN_REFRESH_LOCK);
    acquireLock(owner);
  }

  try {
    return await doRefresh();
  } catch (error) {
    // refresh_token 轮换下后到者必然失败：本地已被换成新 token 时按成功处理，避免误报登录失效
    const latestToken = getStoredAccessToken();
    if (latestToken && latestToken !== tokenBefore) return latestToken;
    throw error;
  } finally {
    releaseLock(owner);
  }
}
